import { vec3 } from 'gl-matrix';
import { Bvh } from './Bvh';

/**
 * Ray queries against a BVH. Iterative descent (no recursion) with an explicit
 * stack of node indices, front-to-back AABB ordering, and far-side culling once
 * a hit is found.
 *
 * Coord conventions:
 *   - rays are parametric: point = origin + t * dir, t in [0, maxT]
 *   - `dir` need not be unit-length; `t` is scaled by its magnitude. Callers
 *     that care about world-space distance should normalize before calling.
 *   - all triangles are double-sided here (no back-face culling). Callers that
 *     want one-sided behavior can check `dot(dir, normal) < 0` on the hit.
 */

export interface BvhHit {
  /** Parametric distance along the ray. */
  t: number;
  /** Triangle ID in the source (0..triCount). */
  triIndex: number;
  /** Face normal at the hit, world-space. NOT necessarily unit-length on input
   *  — `raycastBvh` normalizes before returning. */
  normal: vec3;
}

const EPS = 1e-7;

/** Workspace for `raycastBvh`. Each call reuses the same arrays to avoid alloc. */
const stack = new Int32Array(64);

/** Find nearest hit along ray within [0, maxT]. Returns null on miss. */
export function raycastBvh(
  bvh: Bvh,
  origin: vec3,
  dir: vec3,
  maxT: number,
): BvhHit | null {
  if (bvh.nodeCount === 0) return null;

  const invDx = 1 / dir[0];
  const invDy = 1 / dir[1];
  const invDz = 1 / dir[2];
  const ox = origin[0], oy = origin[1], oz = origin[2];
  const dx = dir[0], dy = dir[1], dz = dir[2];

  let stackTop = 0;
  stack[stackTop++] = 0;

  let bestT = maxT;
  let bestTri = -1;
  let bestNx = 0, bestNy = 0, bestNz = 0;

  const bounds = bvh.nodeBounds;
  const leftFirst = bvh.nodeLeftFirst;
  const triCount = bvh.nodeTriCount;
  const triIds = bvh.triIds;
  const indices = bvh.indices;
  const positions = bvh.positions;

  while (stackTop > 0) {
    const nodeIdx = stack[--stackTop];
    const b = nodeIdx * 6;

    // Slab test against this node's AABB.
    const tEnter = aabbRayEnter(
      bounds[b], bounds[b + 1], bounds[b + 2],
      bounds[b + 3], bounds[b + 4], bounds[b + 5],
      ox, oy, oz, invDx, invDy, invDz,
      bestT,
    );
    if (tEnter < 0) continue;

    const tc = triCount[nodeIdx];
    if (tc > 0) {
      // Leaf — test triangles.
      const first = leftFirst[nodeIdx];
      for (let i = 0; i < tc; i++) {
        const triId = triIds[first + i];
        const i0 = indices[triId * 3] * 3;
        const i1 = indices[triId * 3 + 1] * 3;
        const i2 = indices[triId * 3 + 2] * 3;
        const t = rayTriIntersect(
          ox, oy, oz, dx, dy, dz,
          positions[i0], positions[i0 + 1], positions[i0 + 2],
          positions[i1], positions[i1 + 1], positions[i1 + 2],
          positions[i2], positions[i2 + 1], positions[i2 + 2],
          bestT,
        );
        if (t > 0 && t < bestT) {
          bestT = t;
          bestTri = triId;
          // Face normal = (v1-v0) × (v2-v0).
          const e1x = positions[i1] - positions[i0];
          const e1y = positions[i1 + 1] - positions[i0 + 1];
          const e1z = positions[i1 + 2] - positions[i0 + 2];
          const e2x = positions[i2] - positions[i0];
          const e2y = positions[i2 + 1] - positions[i0 + 1];
          const e2z = positions[i2 + 2] - positions[i0 + 2];
          bestNx = e1y * e2z - e1z * e2y;
          bestNy = e1z * e2x - e1x * e2z;
          bestNz = e1x * e2y - e1y * e2x;
        }
      }
    } else {
      // Internal — push both children. Push far child first so near is popped first.
      const left = leftFirst[nodeIdx];
      const right = left + 1;
      const lb = left * 6;
      const rb = right * 6;
      const tL = aabbRayEnter(
        bounds[lb], bounds[lb + 1], bounds[lb + 2],
        bounds[lb + 3], bounds[lb + 4], bounds[lb + 5],
        ox, oy, oz, invDx, invDy, invDz,
        bestT,
      );
      const tR = aabbRayEnter(
        bounds[rb], bounds[rb + 1], bounds[rb + 2],
        bounds[rb + 3], bounds[rb + 4], bounds[rb + 5],
        ox, oy, oz, invDx, invDy, invDz,
        bestT,
      );
      // Push descending by t so the near child is at the top.
      if (tL < 0 && tR < 0) continue;
      if (tL < 0) {
        stack[stackTop++] = right;
      } else if (tR < 0) {
        stack[stackTop++] = left;
      } else if (tL <= tR) {
        stack[stackTop++] = right;
        stack[stackTop++] = left;
      } else {
        stack[stackTop++] = left;
        stack[stackTop++] = right;
      }
    }
  }

  if (bestTri < 0) return null;

  // Normalize the face normal.
  const nlen = Math.sqrt(bestNx * bestNx + bestNy * bestNy + bestNz * bestNz);
  const inv = nlen > 0 ? 1 / nlen : 0;
  return {
    t: bestT,
    triIndex: bestTri,
    normal: vec3.fromValues(bestNx * inv, bestNy * inv, bestNz * inv),
  };
}

/**
 * Any-hit test along a segment p0 → p1. Returns true if any triangle is hit
 * with `t` in (0, 1). Faster than `raycastBvh` because it exits on first hit.
 */
export function segmentHitBvh(bvh: Bvh, p0: vec3, p1: vec3): boolean {
  if (bvh.nodeCount === 0) return false;

  const ox = p0[0], oy = p0[1], oz = p0[2];
  const dx = p1[0] - ox, dy = p1[1] - oy, dz = p1[2] - oz;
  const invDx = 1 / dx;
  const invDy = 1 / dy;
  const invDz = 1 / dz;

  let stackTop = 0;
  stack[stackTop++] = 0;

  const bounds = bvh.nodeBounds;
  const leftFirst = bvh.nodeLeftFirst;
  const triCount = bvh.nodeTriCount;
  const triIds = bvh.triIds;
  const indices = bvh.indices;
  const positions = bvh.positions;

  while (stackTop > 0) {
    const nodeIdx = stack[--stackTop];
    const b = nodeIdx * 6;

    const tEnter = aabbRayEnter(
      bounds[b], bounds[b + 1], bounds[b + 2],
      bounds[b + 3], bounds[b + 4], bounds[b + 5],
      ox, oy, oz, invDx, invDy, invDz,
      1.0,
    );
    if (tEnter < 0) continue;

    const tc = triCount[nodeIdx];
    if (tc > 0) {
      const first = leftFirst[nodeIdx];
      for (let i = 0; i < tc; i++) {
        const triId = triIds[first + i];
        const i0 = indices[triId * 3] * 3;
        const i1 = indices[triId * 3 + 1] * 3;
        const i2 = indices[triId * 3 + 2] * 3;
        const t = rayTriIntersect(
          ox, oy, oz, dx, dy, dz,
          positions[i0], positions[i0 + 1], positions[i0 + 2],
          positions[i1], positions[i1 + 1], positions[i1 + 2],
          positions[i2], positions[i2 + 1], positions[i2 + 2],
          1.0,
        );
        if (t > EPS && t < 1.0) return true;
      }
    } else {
      const left = leftFirst[nodeIdx];
      stack[stackTop++] = left + 1;
      stack[stackTop++] = left;
    }
  }
  return false;
}

/**
 * Ray–AABB slab test. Returns the smallest t at which the ray enters the box,
 * or -1 if no intersection within [0, maxT].
 */
function aabbRayEnter(
  bMinX: number, bMinY: number, bMinZ: number,
  bMaxX: number, bMaxY: number, bMaxZ: number,
  ox: number, oy: number, oz: number,
  invDx: number, invDy: number, invDz: number,
  maxT: number,
): number {
  let tMin: number, tMax: number;
  if (invDx >= 0) {
    tMin = (bMinX - ox) * invDx;
    tMax = (bMaxX - ox) * invDx;
  } else {
    tMin = (bMaxX - ox) * invDx;
    tMax = (bMinX - ox) * invDx;
  }
  let tyMin: number, tyMax: number;
  if (invDy >= 0) {
    tyMin = (bMinY - oy) * invDy;
    tyMax = (bMaxY - oy) * invDy;
  } else {
    tyMin = (bMaxY - oy) * invDy;
    tyMax = (bMinY - oy) * invDy;
  }
  if (tMin > tyMax || tyMin > tMax) return -1;
  if (tyMin > tMin) tMin = tyMin;
  if (tyMax < tMax) tMax = tyMax;
  let tzMin: number, tzMax: number;
  if (invDz >= 0) {
    tzMin = (bMinZ - oz) * invDz;
    tzMax = (bMaxZ - oz) * invDz;
  } else {
    tzMin = (bMaxZ - oz) * invDz;
    tzMax = (bMinZ - oz) * invDz;
  }
  if (tMin > tzMax || tzMin > tMax) return -1;
  if (tzMin > tMin) tMin = tzMin;
  if (tzMax < tMax) tMax = tzMax;
  if (tMax < 0 || tMin > maxT) return -1;
  return Math.max(0, tMin);
}

/**
 * Möller-Trumbore ray-triangle intersection. Returns t (parametric distance)
 * or -1 on miss. Double-sided (no back-face cull).
 */
function rayTriIntersect(
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  v0x: number, v0y: number, v0z: number,
  v1x: number, v1y: number, v1z: number,
  v2x: number, v2y: number, v2z: number,
  maxT: number,
): number {
  const e1x = v1x - v0x, e1y = v1y - v0y, e1z = v1z - v0z;
  const e2x = v2x - v0x, e2y = v2y - v0y, e2z = v2z - v0z;
  // p = dir × e2
  const px = dy * e2z - dz * e2y;
  const py = dz * e2x - dx * e2z;
  const pz = dx * e2y - dy * e2x;
  const det = e1x * px + e1y * py + e1z * pz;
  if (det > -EPS && det < EPS) return -1;
  const invDet = 1 / det;
  // t = origin - v0
  const tx = ox - v0x, ty = oy - v0y, tz = oz - v0z;
  const u = (tx * px + ty * py + tz * pz) * invDet;
  if (u < 0 || u > 1) return -1;
  // q = t × e1
  const qx = ty * e1z - tz * e1y;
  const qy = tz * e1x - tx * e1z;
  const qz = tx * e1y - ty * e1x;
  const v = (dx * qx + dy * qy + dz * qz) * invDet;
  if (v < 0 || u + v > 1) return -1;
  const t = (e2x * qx + e2y * qy + e2z * qz) * invDet;
  if (t < EPS || t > maxT) return -1;
  return t;
}
